import { Injectable, Injector } from '@angular/core';
import { State } from '~/modules';
import { clone, get, merge, set } from 'lodash-es';
import { BehaviorSubject, interval, throwError } from 'rxjs';
import { debounce, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { loadPersistedStateSuccess } from '~/modules/app/slice';
import { environment } from '#environment';
import { Preferences } from '@capacitor/preferences';
import { SecureStorageService } from '~/shared/services/secure-storage.service';

@Injectable({
  providedIn: 'root'
})
export class PersistenceService {
  // persist only data by given object path to local storage
  whiteListObjectPaths: string[] = [
    ...environment.localStorageWhiteList,
    'cards.favoriteCardId',
    'portfolio.favoriteNumbers',
    'news.readNewsIds',
    'auth.consentAccepted',
    'auth.contractNumber',
    'preference.lang',
    'preference.colorMode',
    'preference.crmFavoriteClientIds'
  ];

  private pendingStatePatch: State = {} as State;
  private readonly updatePersistedState$ = new BehaviorSubject<State>({} as State);
  private readonly stateKey = 'state';
  private readonly debounceInterval = 500;
  private readonly wasLaunchedBeforeKey = 'wasLaunchedBefore';

  constructor(
    private readonly injector: Injector,
    private readonly secureStorageService: SecureStorageService
  ) {}

  async init(): Promise<void> {
    const wasLaunchedBefore = async () => {
      const result = await Preferences.get({ key: this.wasLaunchedBeforeKey });
      return result.value === 'true';
    };

    if (!(await wasLaunchedBefore())) {
      await Preferences.set({
        key: this.wasLaunchedBeforeKey,
        value: 'true'
      });
      await this.secureStorageService.clear();
    }

    this.startStateSync().then(() => {
      this.updatePersistedState$
        .pipe(
          debounce(() => interval(this.debounceInterval)),
          switchMap((state) => this.writeToPersistentStorage(this.stateKey, state).catch((e) => throwError(() => e)))
        )
        .subscribe();
    });
    return Promise.resolve();
  }

  loadMockState(state: State) {
    this.pendingStatePatch = merge({}, this.pendingStatePatch, state);
    this.injector.get<Store>(Store).dispatch(loadPersistedStateSuccess());
  }

  syncState(state: State): State {
    if (Object.keys(this.pendingStatePatch).length > 0) {
      const patchState = clone(this.pendingStatePatch);
      this.pendingStatePatch = {} as State;
      return merge({}, state || {}, patchState);
    }
    return state;
  }

  /**
   * Picks the object paths of the passed State object which match the whitelist and returns a new object containing only those
   * paths and their values. This is used to persist only the whitelisted parts of the state to storage.
   *
   * @param state the state object to pick the whitelisted paths from
   * @returns a new object containing only the whitelisted paths and their values
   */
  pickWhiteListObject(state: State): State {
    return this.whiteListObjectPaths.reduce((whiteListState, path) => {
      const value = get(state, path, undefined);
      if (value === undefined) {
        return whiteListState;
      }
      return merge(whiteListState, set({}, path, value));
    }, {} as State);
  }

  private writeToPersistentStorage(key: string, value: State): Promise<any> {
    return this.secureStorageService.setItem(key, JSON.stringify(value));
  }

  private readFromPersistentStorage(key: string): Promise<any> {
    return this.secureStorageService
      .getItem(key)
      .then((data) => JSON.parse(data.value))
      .catch((e) => console.warn(`Error reading from persistent storage: ${e}`));
  }

  private async startStateSync(): Promise<void> {
    const persistedData = this.pickWhiteListObject(await this.readFromPersistentStorage(this.stateKey));

    return new Promise((resolve) => {
      let isFirstSync = true;
      this.pendingStatePatch = merge({}, this.pendingStatePatch, persistedData);

      this.injector.get<Store>(Store).subscribe((state) => {
        const currentState = isFirstSync ? merge({}, state, persistedData) : state;
        this.updatePersistedState$.next(this.pickWhiteListObject(currentState as State));
        isFirstSync = false;
        resolve();
      });

      // once the pendingStatePatch has been updated, we need to fire an action to trigger an update on the selectors
      this.injector.get<Store>(Store).dispatch(loadPersistedStateSuccess());
    });
  }
}
